import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, BadgeCheck } from "lucide-react";
import { shrinkImage } from "@/lib/shrinkImage";
import { KycFields } from "@/components/KycFields";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Staff counter capture — fill the customer's KYC in person, verified on the spot. */
export default function KycCollect() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { branch: authBranch } = useAuth();
  const branch = (params.get("branch") || authBranch || "DANDENONG").toUpperCase();

  const [form, setForm] = useState({
    name_as_per_passport: "",
    nationality: "",
    licence_number: "",
    licence_state: "",
    licence_expiry: "",
    passport_number: "",
    passport_issue: "",
    passport_expiry: "",
    company_name: "",
    abn: "",
    acn: "",
    address: params.get("address") || "",
    email: params.get("email") || "",
    whatsapp_mobile: params.get("phone") || "",
    customer_name: params.get("name") || "",
  });
  const [accepted, setAccepted] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  const [signature, setSignature] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (file) => {
    setUploading(true);
    try {
      const prepared = await shrinkImage(file);
      const body = new FormData();
      body.append("file", prepared);
      const { data } = await axios.post(`${API}/public/kyc/upload`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setFiles((f) => [
        ...f,
        {
          file_id: data.file_id,
          kind: "kyc_id",
          filename: data.filename || prepared.name,
          content_type: data.content_type,
          size: data.size,
        },
      ]);
      toast.success("Document attached");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not attach that file");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!form.name_as_per_passport.trim()) return toast.error("Enter the name as per passport");
    if (!form.customer_name.trim()) return toast.error("Enter the customer's name");
    if (!form.whatsapp_mobile.trim()) return toast.error("Enter the WhatsApp mobile number");
    if (!privacyOk) return toast.error("Confirm the privacy notice with the customer");
    if (!accepted) return toast.error("Accept the declaration");
    if (files.length < 2) return toast.error("Attach photos of two forms of ID");
    setSaving(true);
    try {
      const { data } = await api.post("/kyc/collect", {
        ...form,
        branch,
        declaration_accepted: accepted,
        privacy_accepted: privacyOk,
        signature,
        attachments: files,
      });
      toast.success("KYC captured and marked verified");
      navigate(`/kyc/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save that");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up" data-testid="kyc-collect-page">
      <button
        onClick={() => navigate(-1)}
        className="mb-5 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        data-testid="kyc-collect-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
        {branch}
      </p>
      <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Collect KYC at the counter</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Fill this in with the customer in front of you and photograph their two forms of ID
        (a signature is optional). It is saved and <strong>marked verified</strong> straight away.
      </p>

      <div className="mt-6 max-w-2xl space-y-4 border border-border bg-white p-6" data-testid="kyc-form">
        <KycFields
          form={form}
          set={set}
          files={files}
          onRemoveFile={(i) => setFiles(files.filter((_, j) => j !== i))}
          upload={upload}
          uploading={uploading}
          signature={signature}
          setSignature={setSignature}
          accepted={accepted}
          setAccepted={setAccepted}
          privacyOk={privacyOk}
          setPrivacyOk={setPrivacyOk}
          branch={branch}
        />

        <Button
          className="w-full rounded-none bg-[#166534] py-6 font-semibold text-white hover:bg-[#14532D]"
          onClick={submit}
          disabled={saving}
          data-testid="kyc-collect-submit"
        >
          <BadgeCheck className="mr-1.5 h-4 w-4" /> {saving ? "Saving…" : "Save & mark verified"}
        </Button>
      </div>
    </div>
  );
}
